<template>
  <div
    class="supplier-mark"
    :class="{ 'supplier-mark-light': theme === 'light' }"
  >
    <div class="section">
      <div class="section-title">我的开标一览表</div>
      <el-table
        :data="selfList"
        class="m-t-24 hall-table"
        size="medium"
        :highlight-current-row="false"
        :row-style="{ fontSize: '22px', textAlign: 'center' }"
        :header-cell-style="{ fontSize: '22px', textAlign: 'center' }"
      >
        <template v-for="(item, index) in defaultMarkList">
          <el-table-column
            v-if="item.markId"
            :key="index"
            :label="
              item.markUnit
                ? `${item.markName}(${item.markUnit})`
                : item.markName
            "
            :align="columnAlign(item)"
            header-align="center"
          >
            <template slot-scope="{ row }">
              <div class="row-content">
                {{ columnContent(item.markId, row) }}
              </div>
              <money-in-cn
                v-if="isMoney(item)"
                :money="columnContent(item.markId, row)"
                :show-prefix="false"
                class="m-t-10"
                :style="{ color: '#27d8fe', fontSize: '22px' }"
                :multiple="getMoneyMultiple(item)"
              />
            </template>
          </el-table-column>
          <el-table-column
            v-else
            :key="index"
            :label="item.markName"
            align="center"
          >
            <template v-slot:default="{ row }">
              <span>{{ getExtraContent(row, item.markName) }}</span>
            </template>
          </el-table-column>
        </template>
        <el-table-column label="开标现场备注" align="center">
          <template slot-scope="{ row }">
            <div class="row-content">{{ row.openBidRemark || '-' }}</div>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <div v-show="otherList.length > 0" class="section m-t-40">
      <div class="section-title">其他开标一览表</div>
      <el-table
        :data="otherList"
        class="m-t-24 hall-table"
        :highlight-current-row="false"
        :row-style="{ fontSize: '22px', textAlign: 'center' }"
        :header-cell-style="{ fontSize: '22px', textAlign: 'center' }"
      >
        <el-table-column
          label="序号"
          prop="index"
          align="center"
          type="index"
          width="80"
        />
        <template v-for="(item, index) in defaultMarkList">
          <el-table-column
            v-if="item.markId"
            :key="index"
            :label="
              item.markUnit
                ? `${item.markName}(${item.markUnit})`
                : item.markName
            "
            :align="columnAlign(item)"
            header-align="center"
          >
            <template slot-scope="{ row }">
              <div class="row-content">
                {{ columnContent(item.markId, row) }}
              </div>
              <money-in-cn
                v-if="isMoney(item)"
                :money="columnContent(item.markId, row)"
                :show-prefix="false"
                class="m-t-10"
                :style="{ color: '#27d8fe', fontSize: '22px' }"
                :multiple="getMoneyMultiple(item)"
              />
            </template>
          </el-table-column>
          <el-table-column
            v-else
            :key="index"
            :label="item.markName"
            align="center"
          >
            <template v-slot:default="{ row }">
              <span>{{ getExtraContent(row, item.markName) }}</span>
            </template>
          </el-table-column>
        </template>

        <el-table-column label="开标现场备注" align="center">
          <template slot-scope="{ row }">
            <div class="row-content">
              {{ row.openBidRemark || row.backRemark || '-' }}
            </div>
          </template>
        </el-table-column>
      </el-table>

      <pagination
        v-if="otherTotal > 0"
        :total="otherTotal"
        :page.sync="otherPage"
        :limit.sync="otherSize"
        :page-sizes="[5, 10]"
        @pagination="handlePagination"
      />
    </div>
  </div>
</template>

<script>
// 接口
import {
  supplierGetMarkApi,
  supplierGetOtherMarkApi
} from '@/api/hall/bid-open-hall'
// 组件
import Pagination from '@/components/common/Pagination.vue'
import MoneyInCn from '@/components/common/MoneyInCn.vue'
// 工具方法
import { strToThousands } from '@/utils/utils'
// 数据字典
import {
  bidFileTypeDict,
  bidMarkTypeDict,
  bidMarkUnitTypeDict
} from '@/dictionary/project/kpb-project'
import { bidSubmitStatusDict } from '@/dictionary/project/bid'
import { fileDecodeStatusDict } from '@/dictionary/hall/open-bid-hall'

export default {
  name: 'BidOpenSupplierMark',

  components: {
    Pagination,
    MoneyInCn
  },

  props: {
    // 项目id
    projectId: {
      type: [String, Number],
      default: 0
    },

    // 投标单位id
    bidderId: {
      type: [String, Number],
      default: 0
    },

    // 开标一览表
    markList: {
      type: Array,
      default: () => []
    },

    theme: {
      validator: function(value) {
        return ['dark', 'light'].indexOf(value) !== -1
      },
      default: 'dark'
    }
  },

  data() {
    return {
      // 自己的开标一览表
      selfList: [],
      selfPage: 1,
      selfSize: 10,

      // 其它供应商的开标一览表
      otherList: [],
      otherPage: 1,
      otherSize: 5,
      otherTotal: 0
    }
  },

  computed: {
    // 商务开标一览表
    defaultMarkList() {
      const tempList = (this.markList || []).filter(
        item => item.bidFileType === bidFileTypeDict.BUSINESS.value
      )
      // 在投标单位后增加文件递交状态和文件解密状态
      const bidderMarkIndex = tempList.findIndex(
        item => item.markType === bidMarkTypeDict.BID_PERSON.value
      )
      const extraMarkList = [
        { markName: '投标单位' },
        { markName: '文件递交状态' },
        { markName: '文件解密状态' }
      ]
      tempList.splice(bidderMarkIndex, 1, ...extraMarkList)
      return tempList
    }
  },

  mounted() {
    // 监听用户离开和回到页面，触发函数，更新数据
    document.addEventListener('visibilitychange', this.visibilityChange)
    // 通过hook监听组件销毁钩子函数
    this.$once('hook:beforeDestroy', () => {
      document.removeEventListener('visibilitychange', this.visibilityChange)
    })
    this.visibilityChange()
  },

  methods: {
    // 页面事件
    /**
     * 离开该窗口页面(即离开当前标签页)，再次回来触发的事件函数
     */
    visibilityChange() {
      this.getSelfMark()
      this.getOtherMarkList()
    },

    columnContent(markId, row) {
      const itemList = row.itemList || []
      const index = itemList.findIndex(item => item.markId === markId)
      const item = itemList[index] || {}
      let content = item.markValue || '-'
      if (
        item.markUnit === bidMarkUnitTypeDict.YUAN.value ||
        item.markUnit === bidMarkUnitTypeDict.TEN_THOUSAND.value
      ) {
        content = strToThousands(content.toString())
      }
      return content
    },

    /**
     * 获取额外信息
     * @param row
     */
    getExtraContent(row, markName) {
      if (markName === '投标单位') {
        return row.bidderName
      }
      if (markName === '文件递交状态') {
        return bidSubmitStatusDict.translate(row.bidSubmitStatus)?.label || '-'
      }
      if (markName === '文件解密状态') {
        return fileDecodeStatusDict.translate(row.bidFileStatus)?.label || '-'
      }
      return '-'
    },

    /**
     * 获取金额的倍率
     */
    getMoneyMultiple(item) {
      if (item.markUnit === bidMarkUnitTypeDict.TEN_THOUSAND.value) {
        return 10000
      }
      return 1
    },

    handlePagination() {
      this.getOtherMarkList()
    },

    // 业务事件
    /**
     * 获取自己的开标一览表结果
     */
    getSelfMark() {
      const params = {
        page: this.selfPage,
        size: this.selfSize
      }
      supplierGetMarkApi(this.projectId, this.bidderId, params)
        .then(res => {
          const { data = {} } = res
          console.log('data', data)
          this.selfList = data.records || []
        })
        .catch(err => {
          if (err?.message) this.$message.error(err.message)
        })
    },

    /**
     * 获取其他开标一览表
     */
    getOtherMarkList() {
      const params = { page: this.otherPage, size: this.otherSize }
      supplierGetOtherMarkApi(this.projectId, this.bidderId, params).then(
        res => {
          const { data = {} } = res
          this.otherList = data.records || []
          this.otherTotal = data.total || 0
        }
      )
    },

    /**
     * 当前列内容文字对齐方式
     */
    columnAlign(item) {
      // 唱标项类型未投标报价和投标保证金时，右对齐，其他居中对齐
      return item.markUnit === bidMarkUnitTypeDict.YUAN.value ||
        item.markUnit === bidMarkUnitTypeDict.TEN_THOUSAND.value
        ? 'right'
        : 'center'
    },

    /**
     * 当前内容为金额类型
     */
    isMoney(item) {
      return (
        item.markUnit === bidMarkUnitTypeDict.YUAN.value ||
        item.markUnit === bidMarkUnitTypeDict.TEN_THOUSAND.value
      )
    }
  }
}
</script>

<style lang="scss" scoped>
.supplier-mark {
  &.supplier-mark-light {
    .section {
      .section-title {
        color: $text-color;
      }
    }
  }

  .section {
    .section-title {
      font-size: 24px;
      color: #feffff;
      @include text-bold;
      display: flex;
      align-items: center;

      &::before {
        content: '';
        display: inline-block;
        width: 4px;
        height: 24px;
        background: $primary-color;
        margin-right: 8px;
      }
    }
  }

  .hall-table {
    .row-content {
      font-size: 22px;
    }
  }
}
</style>
